import React from "react";
import { StickerOrderPreviewSvgProps } from "@mapmaker/renderable";

const DEFAULT_SIZE = 1024;
const MAX_GRID_CELLS = 9;

type StickerOrderPreviewLayout = {
  columns: number;
  rows: number;
};

type StickerOrderPreviewItem = {
  url: string;
  quantity?: number;
};

function getGridLayout(stickerCount: number): StickerOrderPreviewLayout {
  if (stickerCount <= 1) {
    return { columns: 1, rows: 1 };
  }
  if (stickerCount === 2) {
    return { columns: 2, rows: 1 };
  }
  if (stickerCount <= 4) {
    return { columns: 2, rows: 2 };
  }
  if (stickerCount <= 6) {
    return { columns: 3, rows: 2 };
  }
  return { columns: 3, rows: 3 };
}

function normalizeStickers(
  stickerUrls: string[],
  stickerQuantities: number[] = []
): StickerOrderPreviewItem[] {
  return stickerUrls
    .map((url, index) => ({
      url: url?.trim(),
      quantity: stickerQuantities[index],
    }))
    .filter((sticker) => Boolean(sticker.url));
}

export default function StickerOrderPreviewSvg({
  stickerUrls,
  stickerQuantities,
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  responsive = false,
  backgroundColor = "#FFFFFF",
}: StickerOrderPreviewSvgProps<"resource">) {
  const normalizedStickers = normalizeStickers(
    stickerUrls,
    stickerQuantities
  );
  const visibleStickerCount =
    normalizedStickers.length > MAX_GRID_CELLS
      ? MAX_GRID_CELLS - 1
      : normalizedStickers.length;
  const hiddenStickerCount = normalizedStickers.length - visibleStickerCount;
  const showOverflowTile = hiddenStickerCount > 1;
  const cellCount = showOverflowTile ? MAX_GRID_CELLS : visibleStickerCount;
  const { columns, rows } = getGridLayout(Math.max(cellCount, 1));

  const outerPadding = Math.round(Math.min(width, height) * 0.05);
  const gap = Math.round(Math.min(width, height) * 0.025);
  const contentWidth = width - outerPadding * 2;
  const contentHeight = height - outerPadding * 2;
  const tileSize = Math.min(
    (contentWidth - gap * (columns - 1)) / columns,
    (contentHeight - gap * (rows - 1)) / rows
  );
  const gridWidth = tileSize * columns + gap * (columns - 1);
  const gridHeight = tileSize * rows + gap * (rows - 1);
  const gridOffsetX = (width - gridWidth) / 2;
  const gridOffsetY = (height - gridHeight) / 2;
  const badgePadding = tileSize * 0.055;
  const badgeHeight = tileSize * 0.16;
  const badgeWidthMultiplier = tileSize * 0.11;

  function getTilePosition(index: number) {
    return {
      x: gridOffsetX + (index % columns) * (tileSize + gap),
      y: gridOffsetY + Math.floor(index / columns) * (tileSize + gap),
    };
  }

  function renderQuantityBadge(
    sticker: StickerOrderPreviewItem,
    x: number,
    y: number
  ) {
    if (!sticker.quantity || sticker.quantity <= 1) {
      return null;
    }

    const badgeText = `x${sticker.quantity}`;
    const badgeWidth =
      badgePadding * 2 + badgeText.length * badgeWidthMultiplier;

    return (
      <>
        <rect
          x={x + tileSize - badgeWidth - badgePadding}
          y={y + badgePadding}
          width={badgeWidth}
          height={badgeHeight}
          rx={badgeHeight / 2}
          fill="#FFFFFF"
          stroke="#D4D4D8"
        />
        <text
          x={x + tileSize - badgeWidth / 2 - badgePadding}
          y={y + badgePadding + badgeHeight / 2}
          fill="#18181B"
          fontFamily="Helvetica, Arial, sans-serif"
          fontSize={badgeHeight * 0.55}
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {badgeText}
        </text>
      </>
    );
  }

  function renderStickerTile(sticker: StickerOrderPreviewItem, index: number) {
    const { x, y } = getTilePosition(index);

    return (
      <g key={`${sticker.url}-${index}`}>
        <rect
          x={x}
          y={y}
          width={tileSize}
          height={tileSize}
          rx={tileSize * 0.06}
          fill="#FFFFFF"
        />
        <image
          href={sticker.url}
          x={x}
          y={y}
          width={tileSize}
          height={tileSize}
          preserveAspectRatio="xMidYMid meet"
        />
        {renderQuantityBadge(sticker, x, y)}
      </g>
    );
  }

  function renderOverflowTile() {
    if (!showOverflowTile) {
      return null;
    }

    const { x, y } = getTilePosition(MAX_GRID_CELLS - 1);

    return (
      <g key="overflow">
        <rect
          x={x}
          y={y}
          width={tileSize}
          height={tileSize}
          rx={tileSize * 0.06}
          fill="#F4F4F5"
          stroke="#D4D4D8"
        />
        <text
          x={x + tileSize / 2}
          y={y + tileSize / 2 - tileSize * 0.06}
          fill="#18181B"
          fontFamily="Helvetica, Arial, sans-serif"
          fontSize={tileSize * 0.26}
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {`+${hiddenStickerCount}`}
        </text>
        <text
          x={x + tileSize / 2}
          y={y + tileSize / 2 + tileSize * 0.14}
          fill="#52525B"
          fontFamily="Helvetica, Arial, sans-serif"
          fontSize={tileSize * 0.095}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          more
        </text>
      </g>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox={`0 0 ${width} ${height}`}
      width={responsive ? undefined : width}
      height={responsive ? undefined : height}
      preserveAspectRatio={responsive ? "xMidYMid meet" : undefined}
    >
      {backgroundColor ? (
        <rect x={0} y={0} width={width} height={height} fill={backgroundColor} />
      ) : null}
      {normalizedStickers
        .slice(0, visibleStickerCount)
        .map((sticker, index) => renderStickerTile(sticker, index))}
      {renderOverflowTile()}
    </svg>
  );
}
